import { generatePath, useHistory } from 'react-router-dom';
import { FORM_ERROR } from 'final-form';
import queryString from 'query-string';

import { dialogDetails, dialogThanks } from '@urls';
import { useAppContext } from '@contexts/AppContext';
import { getFieldsSingleError, getShafaAbsoluteUrl } from '@utils/common';
import { useUTMLabels } from '@hook/useUTMLabels';
import { sendOutletStartDialog } from '@utils/analytics';

import { useCreateProductDialogMutation } from './gql/graphql.v3.generated';
import { useEncourageStartPurchaseDialogLazyQuery } from './gql/graphql.v5.generated';

interface Payload {
  productId: number,
  body: string,
}

export const useCreateProductDialog = () => {
  const history = useHistory();

  const { isOutlet } = useAppContext();

  const {
    getUTMLabels,
  } = useUTMLabels();

  const [fetchEncourageStartPurchaseDialog] = useEncourageStartPurchaseDialogLazyQuery({
    context: {
      v5: true,
    },
  })

  const [createDialog] = useCreateProductDialogMutation();

  const handleCreateDialog = async ({
    productId,
    body,
  }: Payload): Promise<SingleErrorMapping | null> => {
    const utmLabels = getUTMLabels();

    const [
      createDialogData,
      encourageStartPurchaseDialogData,
    ] = await Promise.all([
      createDialog({
        variables: {
          product: {
            product: productId,
            body,
          },
          utmLabels: utmLabels.lastUtmLabels,
          allUtmLabels: utmLabels.allUtmLabels,
        },
      }),
      fetchEncourageStartPurchaseDialog(),
    ]);

    const thread = createDialogData?.data?.createDialog?.thread;
    const errors = createDialogData?.data?.createDialog?.errors;

    if (errors?.length) {
      const parsedErrors = getFieldsSingleError(errors);

      return {
        ...parsedErrors,
        [FORM_ERROR]: parsedErrors?.__all__,
      };
    }

    if (thread) {
      if (isOutlet) {
        sendOutletStartDialog();
      }
      const dialogPath = generatePath(dialogDetails, {
        threadId: thread.threadId,
      });
      const dialogThanksPath = generatePath(dialogThanks, {
        threadId: thread.threadId,
      });

      if (encourageStartPurchaseDialogData.data?.me?.encourageStartPurchaseDialog) {
        const shafaAbsoluteDialoggUrl = getShafaAbsoluteUrl(
          history.createHref({
            pathname: dialogPath,
          }),
        );

        window.location.href = isOutlet ? shafaAbsoluteDialoggUrl : shafaAbsoluteDialoggUrl;
      } else {
        const redirectSearchParams = queryString.stringify({
          productId,
          username: thread.participant.username,
        });

        window.location.href = history.createHref({
          pathname: dialogThanksPath,
          search: redirectSearchParams,
        });
      }
    }

    return null;
  };

  return handleCreateDialog;
};
